// Org-tier PBX hosts list. One row per registered verso-agent instance.
// Phase 1: read-only, no detail page linked yet (portal renders host detail
// as a card-per-host on the same page — this list-view treatment matches
// the other Verso Cloud list pages instead).
//
// Portal parity note: portal cross-references Commio trunks + GDMS SIP
// servers into each row. The org-tier /pbx-hosts response includes
// commio_trunk as a nested object when set; we render its label. GDMS
// correlation isn't in the current API envelope and is omitted this pass.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ActionMenu from '../components/shared/ActionMenu.jsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';
import { canAdmin } from '../lib/roles.js';
import { parseTs } from '../lib/time.js';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import ListToolbar from '../components/shared/ListToolbar.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import NewAgentModal from '../components/agents/NewAgentModal.jsx';

const PBX_FILTERS = [
  { key: 'all',     label: 'All' },
  { key: 'active',  label: 'Active' },
  { key: 'pending', label: 'Pending' },
  { key: 'offline', label: 'Offline' },
];
import AgentBundleModal from '../components/agents/AgentBundleModal.jsx';
import DeleteAgentModal from '../components/agents/DeleteAgentModal.jsx';
import PbxHostEditModal from '../components/agents/PbxHostEditModal.jsx';

function hostStatusVariant(status, wired, lastSeenTs) {
  if (status) {
    switch (status) {
      case 'online':
      case 'healthy':
      case 'active':
        return 'active';
      case 'degraded': return 'warning';
      case 'offline':
      case 'down':
        return 'critical';
      case 'inactive': return 'inactive';
      default:         return 'unknown';
    }
  }
  // Fallback derivation when the API doesn't set an explicit status.
  if (wired === false) return 'warning';
  if (!lastSeenTs) return 'unknown';
  // Consider agents seen in the last 2 min "active" — matches Verso's
  // heartbeat cadence (30s). Anything older is degraded.
  const nowSec = Math.floor(Date.now() / 1000);
  return (nowSec - lastSeenTs) < 120 ? 'active' : 'warning';
}

function formatLastSeen(ts) {
  if (!ts) return null;
  // Two shapes reach this: epoch seconds from the portal payload, and a D1 string. Only the
  // second is ambiguous, and parseTs owns that — see lib/time.js.
  const date = typeof ts === 'number' ? new Date(ts * 1000) : parseTs(ts);
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

export default function PbxHostsList({ embedded = false }) {
  const { role } = useAuth();
  const canProvision = canAdmin(role);
  const queryClient = useQueryClient();

  const [newOpen, setNewOpen] = useState(false);
  const [bundle,  setBundle]  = useState(null);
  // { agentId → { ok, message, latencyMs, ts } }
  const [testResults, setTestResults] = useState({});
  const [deleting, setDeleting] = useState(null);   // agent row about to be deleted
  const [deleteWarnings, setDeleteWarnings] = useState(null);
  const [editing, setEditing] = useState(null); // agent row whose FreePBX API creds we're editing

  function startTest(agentId) {
    setTestResults((prev) => ({ ...prev, [agentId]: { pending: true } }));
    const started = Date.now();
    api.get(`/agents/${agentId}/proxy/health`, undefined, { timeoutMs: 20_000 })
      .then((data) => {
        setTestResults((prev) => ({
          ...prev,
          [agentId]: {
            ok: true,
            message: data?.ok === false ? 'agent responded but reports unhealthy' : 'reachable',
            latencyMs: Date.now() - started,
            ts: Date.now(),
          },
        }));
      })
      .catch((err) => {
        setTestResults((prev) => ({
          ...prev,
          [agentId]: {
            ok: false,
            message: err?.body?.error || err?.message || 'unreachable',
            latencyMs: Date.now() - started,
            ts: Date.now(),
          },
        }));
      });
  }

  const editMutation = useMutation({
    mutationFn: ({ agentId, body }) => api.put(`/agents/${agentId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pbx-hosts'] });
      queryClient.invalidateQueries({ queryKey: ['agents-list'] });
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (agentId) => api.delete(`/agents/${agentId}`, undefined, undefined, { timeoutMs: 60_000 }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['pbx-hosts'] });
      queryClient.invalidateQueries({ queryKey: ['agents-list'] });
      const warnings = res?.cloudflare_warnings || [];
      if (warnings.length) {
        setDeleteWarnings(warnings);
      } else {
        setDeleting(null);
      }
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['pbx-hosts'],
    queryFn: () => api.get('/pbx-hosts'),
  });

  const provisionMutation = useMutation({
    // Provisioning walks 5+ Cloudflare API calls — use a longer timeout.
    mutationFn: (body) => api.post('/agents/provision', body, undefined, { timeoutMs: 120_000 }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['pbx-hosts'] });
      queryClient.invalidateQueries({ queryKey: ['agents-list'] });
      setNewOpen(false);
      setBundle(result);
    },
  });

  const allRows = useMemo(() => {
    if (Array.isArray(data))         return data;
    if (Array.isArray(data?.hosts))  return data.hosts;
    if (Array.isArray(data?.rows))   return data.rows;
    return [];
  }, [data]);

  const [pbxFilter, setPbxFilter] = useState('all');
  const [search, setSearch] = useState('');
  const stateOf = (h) => {
    const s = String(h.status || '').toLowerCase();
    if (s === 'active')  return 'active';
    if (s === 'pending') return 'pending';
    return 'offline';   // inactive / disconnected / unknown / never-seen
  };
  const filterCounts = useMemo(() => ({
    all:     allRows.length,
    active:  allRows.filter((h) => stateOf(h) === 'active').length,
    pending: allRows.filter((h) => stateOf(h) === 'pending').length,
    offline: allRows.filter((h) => stateOf(h) === 'offline').length,
  }), [allRows]);
  const rows = useMemo(() => {
    let out = allRows;
    if (pbxFilter !== 'all') out = out.filter((h) => stateOf(h) === pbxFilter);
    const s = search.trim().toLowerCase();
    if (s) {
      out = out.filter((h) => {
        const name = String(h.name || h.label || '').toLowerCase();
        const id = String(h.id || h.agent_id || '').toLowerCase();
        const ep = String(h.endpoint_url || h.endpointUrl || '').toLowerCase();
        const ver = String(h.version || '').toLowerCase();
        return name.includes(s) || id.includes(s) || ep.includes(s) || ver.includes(s);
      });
    }
    return out;
  }, [allRows, pbxFilter, search]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        {embedded ? (
          <span className="text-sm text-gray-500">
            PBX hosts running a verso-agent — one per on-prem PBX.
          </span>
        ) : (
          <h1 className="text-2xl font-bold text-gray-900">PBX Hosts</h1>
        )}
        <div className="flex items-center gap-3">
          {allRows.length > 0 && (
            <span className="text-sm text-gray-500">
              {rows.length === allRows.length
                ? `${allRows.length} host${allRows.length === 1 ? '' : 's'}`
                : `${rows.length} of ${allRows.length}`}
            </span>
          )}
          {canProvision && (
            <button
              onClick={() => { setNewOpen(true); provisionMutation.reset(); }}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              New PBX Host
            </button>
          )}
        </div>
      </div>

      {newOpen && (
        <NewAgentModal
          onCancel={() => { setNewOpen(false); provisionMutation.reset(); }}
          onSubmit={(body) => provisionMutation.mutate(body)}
          isPending={provisionMutation.isPending}
          error={provisionMutation.isError
            ? (provisionMutation.error?.body?.error || provisionMutation.error?.message || 'Provision failed')
            : null}
        />
      )}

      {bundle && (
        <AgentBundleModal
          bundle={bundle}
          onClose={() => setBundle(null)}
        />
      )}

      {editing && (
        <PbxHostEditModal
          agent={editing}
          onCancel={() => { setEditing(null); editMutation.reset(); }}
          onSave={(body) => editMutation.mutate({
            agentId: editing.id || editing.agent_id,
            body,
          })}
          isPending={editMutation.isPending}
          error={editMutation.isError
            ? (editMutation.error?.body?.error || editMutation.error?.message || 'Save failed')
            : null}
        />
      )}

      {deleting && (
        <DeleteAgentModal
          agent={deleting}
          isPending={deleteMutation.isPending}
          error={deleteMutation.isError
            ? (deleteMutation.error?.body?.error || deleteMutation.error?.message || 'Delete failed')
            : null}
          warnings={deleteWarnings}
          onCancel={() => {
            setDeleting(null);
            setDeleteWarnings(null);
            deleteMutation.reset();
          }}
          onConfirm={() => {
            const id = deleting.id || deleting.agent_id;
            deleteMutation.mutate(id);
          }}
        />
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <ListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by name, ID, endpoint, or version…"
        filters={PBX_FILTERS}
        filterCounts={filterCounts}
        activeFilter={pbxFilter}
        onFilterChange={setPbxFilter}
      />

      <div className="rounded-lg border border-gray-200 bg-white">
        {isLoading ? (
          <LoadingSpinner message="Loading PBX hosts..." />
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-gray-500">No PBX hosts registered yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Endpoint</th>
                <th className="px-4 py-2">Version</th>
                <th className="px-4 py-2">Last seen</th>
                <th className="px-4 py-2">Commio trunk</th>
                <th className="px-4 py-2">GDMS SIP server</th>
                {canProvision && <th className="px-2 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((h) => {
                const id = h.id || h.agent_id;
                const name = h.label || h.name || id;
                const endpoint = h.endpoint_url
                  || h.endpointUrl
                  || h.hostname
                  || h.tunnel_hostname;
                const lastSeenTs = h.last_seen ?? h.lastSeen ?? h.last_seen_at;
                const lastSeen = formatLastSeen(lastSeenTs);
                const statusLabel = h.status
                  || (h.wired === false ? 'not wired' : (lastSeen ? 'seen' : 'unknown'));
                const commioLabel = h.commio_trunk?.label
                  || h.commioTrunk?.label
                  || h.commio?.outbound?.name;
                const test = testResults[id];
                return (
                  <tr key={id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <StatusBadge
                        status={hostStatusVariant(h.status, h.wired, lastSeenTs)}
                        label={statusLabel}
                      />
                      {test && !test.pending && (
                        <div
                          className={`mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${
                            test.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}
                          title={test.message}
                        >
                          <span>{test.ok ? '✓ reachable' : '✗ ' + test.message}</span>
                          <span className="text-gray-400">{test.latencyMs}ms</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium">
                      <Link to={`/pbx-hosts/${id}`} className="text-blue-600 hover:underline" title={id}>{name}</Link>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600">
                      {endpoint || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600">
                      {h.version || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {lastSeen || <span className="text-gray-300">never</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {commioLabel || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {h.gdms_sip_server?.serverName ? (
                        <span title={h.gdms_sip_server.sipServer || undefined}>
                          {h.gdms_sip_server.serverName}
                        </span>
                      ) : h.gdms_sip_server_id ? (
                        <span className="font-mono text-xs text-gray-500">#{h.gdms_sip_server_id}</span>
                      ) : (
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                          not set
                        </span>
                      )}
                    </td>
                    {canProvision && (
                      <td className="px-2 py-2 text-right">
                        <ActionMenu
                          title={`Actions for ${name}`}
                          items={[
                            { label: test?.pending ? 'Testing…' : 'Test connection', disabled: test?.pending, onClick: () => startTest(id) },
                            {
                              label: h.pbx_type === 'freepbx' && !h.has_freepbx_client_secret ? 'Edit… (no FreePBX API creds)' : 'Edit',
                              onClick: () => { setEditing(h); editMutation.reset(); },
                            },
                            { divider: true },
                            { label: 'Delete', danger: true, onClick: () => { setDeleting(h); setDeleteWarnings(null); deleteMutation.reset(); } },
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
